import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * 렌더 에러 안전망 (BIZ-41). web·admin이 공유한다.
 *
 * React는 렌더 중 에러가 하나라도 나면 트리 전체를 언마운트해 화면이 흰색이 된다.
 * (데이터 응답은 200이어도 그 응답을 그리다 throw하면 같은 증상.) 이 바운더리가
 * 그 에러를 잡아 폴백 화면을 대신 보여준다. 클래스 컴포넌트만 에러 바운더리가 될 수 있다.
 *
 * `resetKeys`가 바뀌면(예: 라우트 경로) 에러 상태를 자동 해제한다 — 에러 난 화면에서
 * 다른 곳으로 이동하면 자연히 회복되게. 로깅은 지금 콘솔이며, 추후 원격 리포팅은 onError로.
 */

interface Props {
  children: ReactNode
  /** 커스텀 폴백. 함수면 (error, reset)을 받는다. 없으면 기본 안내 화면. */
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode)
  /** 값이 바뀌면 에러 상태를 자동 해제한다(얕은 비교). */
  resetKeys?: readonly unknown[]
  /** 에러 로깅 훅(원격 리포팅 연결 자리). */
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // 기본은 콘솔. 추후 원격 로깅은 onError로 연결한다.
    console.error('[ErrorBoundary]', error, info.componentStack)
    this.props.onError?.(error, info)
  }

  override componentDidUpdate(prev: Props): void {
    const { resetKeys } = this.props
    if (this.state.error && prev.resetKeys && resetKeys && !sameKeys(prev.resetKeys, resetKeys)) {
      this.reset()
    }
  }

  reset = (): void => {
    if (this.state.error) this.setState({ error: null })
  }

  override render(): ReactNode {
    const { error } = this.state
    if (!error) return this.props.children

    const { fallback } = this.props
    if (typeof fallback === 'function') return fallback(error, this.reset)
    if (fallback !== undefined) return fallback
    return <DefaultFallback error={error} onReset={this.reset} />
  }
}

function sameKeys(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((v, i) => Object.is(v, b[i]))
}

/** Vite 개발 빌드에서만 true. 프로덕션 사용자에겐 원본 에러 메시지를 노출하지 않는다. */
const IS_DEV = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true

function DefaultFallback({ error, onReset }: { error: Error; onReset: () => void }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--color-bg-page)',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 440, textAlign: 'center' }}>
        <div
          aria-hidden="true"
          style={{
            width: 48,
            height: 48,
            margin: '0 auto 16px',
            display: 'grid',
            placeItems: 'center',
            borderRadius: '50%',
            background: 'var(--color-bg-subtle, rgba(0,0,0,0.05))',
            color: 'var(--color-text-muted)',
          }}
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.3 3.9 2.5 18a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
            <path d="M12 9v4M12 17h.01" />
          </svg>
        </div>
        <h1 style={{ fontSize: 'var(--text-title, 20px)', fontWeight: 700, margin: '0 0 8px', color: 'var(--color-text)' }}>
          문제가 발생했어요
        </h1>
        <p style={{ margin: '0 0 20px', color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          화면을 그리는 중 오류가 났습니다. 새로고침하면 대부분 해결됩니다. 계속되면 담당자에게 알려주세요.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button type="button" data-variant="primary" onClick={() => window.location.reload()}>
            새로고침
          </button>
          <button type="button" data-variant="secondary" onClick={onReset}>
            다시 시도
          </button>
        </div>
        {IS_DEV && (
          <pre
            style={{
              marginTop: 20,
              padding: 12,
              textAlign: 'left',
              fontSize: 12,
              color: 'var(--color-text-muted)',
              background: 'var(--color-bg-subtle, rgba(0,0,0,0.04))',
              borderRadius: 8,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {error.message}
          </pre>
        )}
      </div>
    </div>
  )
}
